import React, { useState } from 'react';
import { AppButton, AppCheckbox, AppPopover } from '@/components/atoms';
import { OWNER_FRANCHISE_NAME } from '../../constants/ownerScope';
import type { OwnerClinic } from '../../services/clinicScope';

/**
 * 担当拠点フィルター — the topbar dropdown that scopes the whole screen to a
 * subset of the owner's 拠点.
 *
 * R-06 pins it to the far right of the topbar. R-04 makes it more than a list
 * filter: the system metric tiles are counted over the selected 拠点, so
 * changing the selection has to recompute them too.
 *
 * `AppPopover` supplies the positioning, the outside-click dismissal and
 * the viewport flip that keep the 280px panel on screen at the right edge —
 * the ConceptDesign's own `left: 0` panel runs off it.
 */

interface RahClinicFilterProps {
  clinics: OwnerClinic[];
  /** Names of the currently selected 拠点. */
  selected: string[];
  onChange: (selected: string[]) => void;
}

export const RahClinicFilter: React.FC<RahClinicFilterProps> = ({
  clinics,
  selected,
  onChange,
}) => {
  const [open, setOpen] = useState(false);

  const content = (
    <div style={{ width: 250 }}>
      <div className="cf-actions" style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <AppButton size="small" onClick={() => onChange(clinics.map(c => c.name))}>
          全選択
        </AppButton>
        <AppButton size="small" onClick={() => onChange([])}>
          全解除
        </AppButton>
      </div>

      <AppCheckbox.Group
        value={selected}
        onChange={values => onChange(values as string[])}
        style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}
        options={clinics.map(clinic => ({
          value: clinic.name,
          label: (
            <span style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <span className="cf-name">{clinic.name}</span>
              <code className="cf-code">{clinic.code}</code>
            </span>
          ),
        }))}
      />

      <div className="cf-foot" style={{ marginTop: 8 }}>
        ※ チェックした拠点のみ表示
      </div>
    </div>
  );

  return (
    <div className="clinic-filter-wrap">
      <AppPopover
        open={open}
        onOpenChange={setOpen}
        trigger="click"
        placement="bottomRight"
        // Portalled to `document.body`: without this the panel sits outside
        // `.rah-shell`, where none of its CSS or design tokens reach.
        classNames={{ root: 'rah-screen' }}
        title={
          <span className="cf-title">
            <span>📍</span> 担当拠点フィルター
          </span>
        }
        content={content}
      >
        <button className={`clinic-filter-trigger${open ? ' open' : ''}`} aria-expanded={open}>
          <span className="cf-ico">🏠</span>
          <span className="cf-label">
            {OWNER_FRANCHISE_NAME} · {selected.length}/{clinics.length}拠点
          </span>
          <span className="cf-caret">▾</span>
        </button>
      </AppPopover>
    </div>
  );
};

export default RahClinicFilter;
